/**
 * Remembers the element that has focus now and returns a function that gives it focus back.
 *
 * Call it when opening a modal or page, and run the returned function once the modal is gone.
 * When the original element no longer exists (for example a table row that was re-rendered or
 * deleted), focus goes to the element returned by `fallback`.
 *
 * @param fallback Resolves a stable element to focus when the original one is gone.
 * @returns A function that restores focus.
 * @example
 * const restoreFocus = captureFocus(() => document.querySelector<HTMLElement>('#create-button'));
 * // ...later, after the modal closes
 * restoreFocus();
 */
export function captureFocus(fallback?: () => HTMLElement | null): () => void {
  const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
  return () => {
    if (opener?.isConnected) {
      opener.focus();
      return;
    }
    fallback?.()?.focus();
  };
}
